import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U2106';
import { doChangePassword, luHandleAppiError, type U2106ChangePasswordPayload } from '../U2106Service';

export type U2106ChangePasswordFormValues = U2106ChangePasswordPayload;

export type UseU2106ChangePasswordHandlersReturn = {
  loading: boolean;
  submitChangePassword: (values: U2106ChangePasswordFormValues) => Promise<boolean>;
};

export function useU2106ChangePasswordHandlers(): UseU2106ChangePasswordHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const submitChangePassword = useCallback(
    async (values: U2106ChangePasswordFormValues): Promise<boolean> => {
      setLoading(true);
      try {
        await doChangePassword(axios, values);
        toast.success(t('changePassword.success', { ns: NS }));
        return true;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        } else {
          toast.error(t('changePassword.errors.generic', { ns: NS }));
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [axios, t, toast],
  );

  return { loading, submitChangePassword };
}
